import { ROUTES } from '@/constants/constants';
import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { fireSubscribedConversion, logEvent } from '@/helpers/eventLogger';
import { useNotifications } from '@/hooks/useNotifications';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { confirmPaymentIntent, createSetupIntent, getStripePublicKey, processPayment } from '@editar-pdf/api';
import { FormEvent, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { buildStripeAppearance } from './appearance';
import { FormSkeleton } from './FormSkeleton';

/** What the dialog needs to know to drive its footer, its close guard and its error state. */
export interface PaymentFormStatus {
  /** The PaymentElement has mounted and can be submitted. */
  ready: boolean;
  /** A payment is in flight. */
  submitting: boolean;
  /** Stripe could not be set up at all — the dialog offers a retry instead of the form. */
  failed: boolean;
}

const IDLE_STATUS: PaymentFormStatus = { ready: false, submitting: false, failed: false };

type Props = {
  /** Ties the dialog footer's submit button to this form; they are not DOM siblings. */
  formId: string;
  documentId?: string;
  subscriptionType: string;
  /** Bumped by the dialog's Retry button to re-run the Stripe setup calls. */
  reloadToken?: number;
  onSubscriptionSuccess?: () => void;
  onCancel?: () => void;
  onStatusChange?: (status: PaymentFormStatus) => void;
};

type FormProps = Omit<Props, 'onSubscriptionSuccess'> & {
  onPaymentError?: (message: string) => void;
  onPaymentSuccess?: () => void;
};

function PolicyLink({ onClick, to, children }: PropsWithChildren<{ to: string; onClick?: () => void }>) {
  return (
    <Link onClick={onClick} to={to} className="text-primary underline-offset-4 hover:underline">
      {children}
    </Link>
  );
}

function CheckoutForm({
  formId,
  documentId,
  subscriptionType,
  onPaymentError,
  onPaymentSuccess,
  onCancel,
  onStatusChange,
}: FormProps) {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const { t: tt } = useTranslation('web');
  const stripe = useStripe();
  const elements = useElements();

  const [ready, setReady] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch the client IP once on mount and keep the promise, so it is ready by
  // submit time without adding a third-party round-trip to the payment-critical
  // path. The IP is required for `mandate_data.customer_acceptance.online`.
  // React 19's `useRef` requires an initial value, so the empty slot is now explicit.
  const ipPromiseRef = useRef<Promise<string> | undefined>(undefined);
  const ensureIp = useCallback((): Promise<string> => {
    const cached = ipPromiseRef.current;
    if (cached) return cached;

    const fetchIp = (): Promise<string> =>
      fetch('https://api.ipify.org?format=json')
        .then((response) => response.json())
        .then(({ ip }) => ip as string);

    // Retry once on failure; clear the cache on a final failure so a later
    // submit re-attempts instead of being stuck on a rejected promise.
    const pending = fetchIp()
      .catch(() => fetchIp())
      .catch((e) => {
        ipPromiseRef.current = undefined;
        throw e;
      });

    ipPromiseRef.current = pending;
    return pending;
  }, []);

  useEffect(() => {
    void ensureIp().catch(() => undefined);
  }, [ensureIp]);

  // Mirror readiness and the in-flight state up so the dialog can enable its footer button
  // and refuse to close mid-submit (closing would unmount <Elements> and destroy the
  // PaymentElement, which makes the in-flight Stripe calls throw "This Element has already
  // been destroyed").
  const submittable = Boolean(stripe && elements && loaded && ready);
  useEffect(() => {
    onStatusChange?.({ ready: submittable, submitting: loading, failed: false });
  }, [submittable, loading, onStatusChange]);

  useEffect(() => () => onStatusChange?.({ ready: false, submitting: false, failed: false }), [onStatusChange]);

  // If the form unmounts mid-submit (e.g. navigation, or `needPayment` toggled
  // elsewhere) the PaymentElement is destroyed; don't start further Stripe calls
  // against it. The dialog's close guard already blocks the common case.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements || !loaded || !ready) {
      return;
    }

    logEvent({ logType: 'EVENT', eventType: 'CHECKOUT_FORM_SUBMITTED' });
    setLoading(true);

    try {
      const ip = await ensureIp();
      if (!mountedRef.current) return;

      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${location.origin}${ROUTES.subscribed(i18n.language)}?tariff=${subscriptionType}&documentId=${
            documentId ?? ''
          }`,
          mandate_data: {
            customer_acceptance: {
              type: 'online',
              online: {
                ip_address: ip,
                user_agent: window.navigator.userAgent,
              },
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        if (result.error.type === 'api_connection_error') {
          onPaymentError?.(tt('common.networkError'));
        } else {
          onPaymentError?.(result.error.message ?? t('error'));
        }
      } else {
        const { payment_method } = result.setupIntent;

        if (payment_method) {
          const paymentResponse = await processPayment({
            paymentMethodCode: typeof payment_method === 'string' ? payment_method : '',
            subscriptionType,
          });

          if (paymentResponse.data.stripeId) {
            if (!mountedRef.current) return;
            const paymentResult = await stripe.confirmCardPayment(paymentResponse.data.stripeId);

            if (paymentResult.paymentIntent) {
              const paymentIntentId = paymentResult.paymentIntent.id;

              const confirmPaymentResponse = await confirmPaymentIntent({
                paymentIntentId: paymentIntentId,
                subscriptionType: subscriptionType,
              });

              if (confirmPaymentResponse.status !== 200) {
                console.error('Payment confirmation error:', confirmPaymentResponse);
                onPaymentError?.('Error confirming payment, please try again.');
              } else {
                onPaymentSuccess?.();
              }
            }

            if (paymentResult.error) {
              console.error('Payment confirmation error:', paymentResult.error);
              onPaymentError?.(paymentResult.error.message || tt('common.error'));
            } else {
              onPaymentSuccess?.();
            }
          } else {
            onPaymentError?.(tt('common.error'));
          }
        } else {
          onPaymentError?.(tt('common.error'));
        }
      }
    } catch (e) {
      onPaymentError?.(getResponseError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
      {stripe && elements ? (
        <PaymentElement
          onLoaderStart={() => setReady(true)}
          onReady={() => setLoaded(true)}
          options={{
            readOnly: loading,
            fields: {
              billingDetails: {
                address: {},
              },
            },
            terms: { card: 'never' },
          }}
        />
      ) : (
        <FormSkeleton />
      )}

      {!ready && <FormSkeleton />}

      {/* The consent line and the card mandate in one sentence, deliberately not translated.
          Stripe's PaymentElement localises its own mandate to the browser locale, but the
          terms it points at are written in English, so the sentence the cardholder agrees to
          is shown in that language on every locale. `terms.card: 'never'` on the element
          hides Stripe's copy; the authorization here stands in for it, so the obligation to
          show the mandate (and the `customer_acceptance` sent on submit) is still met. */}
      {loaded && (
        <p className="text-label text-pretty">
          By continuing, you agree to the{' '}
          <PolicyLink to={ROUTES.terms(i18n.language)} onClick={onCancel}>
            Terms of Use &amp; Service
          </PolicyLink>{' '}
          and our{' '}
          <PolicyLink to={ROUTES.policy(i18n.language)} onClick={onCancel}>
            Privacy Policy
          </PolicyLink>
          , and authorize SYNTERIQ LTD to charge your card for this and future payments.
        </p>
      )}
    </form>
  );
}

function PaymentForm({
  formId,
  documentId,
  subscriptionType,
  reloadToken = 0,
  onSubscriptionSuccess,
  onCancel,
  onStatusChange,
}: Props) {
  const { t } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const { notifySuccess, notifyError } = useNotifications();
  const {
    state: { userInfo },
  } = useContext(Context);

  const [options, setOptions] = useState<StripeElementsOptions>();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [failed, setFailed] = useState(false);
  const [innerStatus, setInnerStatus] = useState<PaymentFormStatus>(IDLE_STATUS);

  const handlePaymentSuccess = useCallback(() => {
    fireSubscribedConversion(userInfo?.email);
    logEvent({ logType: 'EVENT', eventType: 'CHECKOUT_SUCCEEDED' });
    notifySuccess(t('success'));
    onSubscriptionSuccess?.();
  }, [notifySuccess, onSubscriptionSuccess, t, userInfo?.email]);

  // Neither of the two setup calls toasts on failure any more: the dialog swaps its whole
  // body for a retry block, which says the same thing where the user is already looking.
  const loadIntent = useCallback(() => {
    createSetupIntent()
      .then((setupResp) =>
        setOptions({
          clientSecret: setupResp.data,
          appearance: buildStripeAppearance(),
        }),
      )
      .catch((e) => {
        logEvent({ logType: 'ERROR', eventType: 'CHECKOUT_ERROR', trace: getResponseError(e) });
        setFailed(true);
      });
  }, []);

  // A payment error is not a setup failure: the card was declined or the intent went stale,
  // so this keeps its toast and silently rebuilds the intent rather than showing the retry
  // block, which would throw away a form the user has already filled in.
  const handleChangeIntent = useCallback(
    (message: string) => {
      logEvent({ logType: 'ERROR', eventType: 'CHECKOUT_ERROR', trace: message });
      notifyError(message);
      setOptions(undefined);
      loadIntent();
    },
    [loadIntent, notifyError],
  );

  // `reloadToken` is the dialog's retry signal — it re-runs both setup calls when the user
  // presses Retry, without the dialog needing a handle on this component.
  useEffect(() => {
    setFailed(false);

    getStripePublicKey()
      .then((keyResp) => loadStripe(keyResp.data))
      .then(setStripe)
      .catch((e) => {
        logEvent({ logType: 'ERROR', eventType: 'CHECKOUT_ERROR', trace: getResponseError(e) });
        setFailed(true);
      });

    loadIntent();
  }, [loadIntent, reloadToken]);

  // One status reaches the dialog, merged here rather than raced between two reporters.
  // `getStripePublicKey` and `createSetupIntent` fail independently: when only the key fails
  // the intent still arrives, CheckoutForm still mounts, and its `failed: false` would
  // otherwise clear a failure that has genuinely left <Elements> without a Stripe instance.
  useEffect(() => {
    onStatusChange?.({
      ready: failed ? false : innerStatus.ready,
      submitting: innerStatus.submitting,
      failed,
    });
  }, [failed, innerStatus, onStatusChange]);

  return options ? (
    <Elements stripe={stripe} options={options}>
      <CheckoutForm
        formId={formId}
        documentId={documentId}
        subscriptionType={subscriptionType}
        onPaymentError={handleChangeIntent}
        onPaymentSuccess={handlePaymentSuccess}
        onCancel={onCancel}
        onStatusChange={setInnerStatus}
      />
    </Elements>
  ) : (
    <FormSkeleton />
  );
}

export { PaymentForm };
